import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Pagination from 'components/Pagination';
import ShipmentDistributor from './modals/ShipmentDistributor';

import { GET, httpOk } from 'helpers/api';
import { formatDate, formatMoney } from 'helpers/helpers';
import { Link } from 'react-router-dom';

import { ReactComponent as FgWarehouseIcon } from 'assets/icons/sidebar/fg_warehouse.svg';
import { ReactComponent as ReportUpIcon } from 'assets/icons/report_up.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import { ReactComponent as TruckIcon } from 'assets/icons/truck.svg';
import CustomSelect from 'components/CustomSelect';

function Dashboard() {
    const { t } = useTranslation()

    const views = [
        {
            id: 'products',
            name: t('products')
        },
        {
            id: 'boxes',
            name: t('boxes')
        },
    ]

    const initFilterData = {
        page: 1,
        page_size: 20,
        view: 'products',
    }

    const [shipmentDistributorModal, setShipmentDistributorModal] = useState(false)

    const [filterData, setFilterData] = useState(initFilterData)

    const [data, setData] = useState({
        in_progress_orders: [],
        planned_orders: [],
        counters: {},
    })

    const handlePaginate = (args) => {
        setFilterData(prev => ({
            ...prev,
            page: args.page + 1,
            page_size: args.page_size
        }));
    };

    const getData = useCallback(async () => {
        const [response, responseStats] = await Promise.all([
            GET('/inventory/fg-warehouse/dashboard/', filterData),
            GET('/inventory/fg-warehouse/items/', filterData),
        ]);

        if (httpOk(response)) {
            setData({ ...response.data, ...responseStats.data });
        }
    }, [filterData]);

    useEffect(() => {
        getData();
    }, [getData]);

    return (
        <>
            <div className="d-between gap-8">
                <div>
                    <div className="main-content-title">
                        {t('fg_warehouse_dashboard')}
                    </div>

                    <div className="main-content-subtitle">
                        {t('fg_and_shipment_management_desc')}
                    </div>
                </div>

                <CustomSelect
                    options={views}
                    value={views.find(option => option.id === filterData?.view) || ''}
                    onChange={(option) => setFilterData({ ...filterData, view: option?.id })}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    isClearable={false}
                // placeholder={t('select')}
                />

            </div>

            <div className="grid4 gap-16">
                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('total_balance')}
                            </div>
                            <div className="value primary">
                                {formatMoney(data?.summary?.total_quantity)} {data?.summary?.unit_short_name}
                            </div>
                        </div>

                        <div className="icon primary">
                            <FgWarehouseIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('reserved')}
                            </div>
                            <div className="value text-warning">
                                {formatMoney(data?.summary?.reserved_total)} {data?.summary?.unit_short_name}
                            </div>
                        </div>

                        <div className="icon warning">
                            <WarningIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('available')}
                            </div>
                            <div className="value text-success">
                                {formatMoney(data?.summary?.available_total)} {data?.summary?.unit_short_name}
                            </div>
                        </div>

                        <div className="icon success">
                            <ReportUpIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('orders_for_shipment')}
                            </div>
                            <div className="value text-orange">
                                {formatMoney(data?.summary?.pending_orders)}
                            </div>
                        </div>

                        <div className="icon orange">
                            <TruckIcon />
                        </div>
                    </div>
                </div>
            </div>

            {data?.low_available?.length > 0 &&
                <div className="alert second alert-warning">
                    <div className="alert-title">
                        <WarningIcon />
                        {t('low_free_stock_alert')}
                    </div>

                    <div className="mb-16">
                        {t('low_stock_warning_desc')}
                    </div>

                    <div className="d-flex flex-column gap-8">
                        {data.low_available.map((item, index) => (
                            <div className="card second" key={index}>
                                <div className="d-between">
                                    <div>
                                        <div className="text-first">
                                            {item.product_name}
                                        </div>
                                        <div className="text-fifth fz12">
                                            {t('available')}: {formatMoney(item.available_quantity)} {item.unit_short_name} | {t('reserved')}: {formatMoney(item.reserved_quantity)} {item.unit_short_name}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                        }
                    </div>
                </div>
            }

            {data?.orders?.length > 0 &&
                <div className="card">
                    <div className="d-between mb-30">
                        <div className="fz16 text-third">
                            {t('orders_to_process')}
                        </div>
                    </div>

                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ width: '10%' }}>{t('order_number')}</th>
                                <th style={{ width: '45%' }}>{t('distributor')}</th>
                                <th style={{ minWidth: '110px' }}>{t('created_at')}</th>
                                <th style={{ width: '20%' }}>{t('status')}</th>
                                {/* <th style={{ width: '15%' }}>{t('actions')}</th> */}
                            </tr>
                        </thead>

                        <tbody>
                            {data?.orders?.map((item, index) => (
                                <tr key={index}>
                                    <td className="text-second">{item.order_number}</td>
                                    <td>{item.distributor_name}</td>
                                    <td>{formatDate(item.created_at)}</td>
                                    <td>
                                        <div className={`alert alert-${item.status} w-fit-content`}>
                                            {t(item.status_label)}
                                        </div>
                                    </td>
                                    {/* <td>
                                    <div className="d-flex align-items-center gap-8">
                                        <button className="white small">
                                            {t('process')}
                                        </button>
                                    </div>
                                </td> */}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <Pagination
                        paginate={handlePaginate}
                        pagination={{
                            total: data?.count || 0
                        }}
                    />
                </div>
            }

            {data?.items?.length > 0 &&

                <div className="card">
                    <div className="d-between mb-30">
                        <div className="fz16 text-third">
                            {t('finished_goods_inventory')}
                        </div>

                        <Link to="/warehouse-finished/warehouse-fg">
                            <button className="dark">
                                {t('warehouse_management')}
                            </button>
                        </Link>
                    </div>

                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ width: '10%' }}>{t('order_number')}</th>
                                <th style={{ width: '45%' }}>{t('distributor')}</th>
                                <th style={{ minWidth: '110px' }}>{t('created_at')}</th>
                                <th style={{ width: '20%' }}>{t('status')}</th>
                            </tr>
                        </thead>

                        <tbody>
                            {data?.items?.map((item, index) => (
                                <tr key={index}>
                                    <td className="text-second">{item.product_code}</td>
                                    <td>{item.product_name}</td>
                                    <td>{formatDate(item.last_updated)}</td>
                                    <td>
                                        <div className={`alert alert-${item.status} w-fit-content`}>
                                            {t(item.status)}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <Pagination
                        paginate={handlePaginate}
                        pagination={{
                            total: data?.count || 0
                        }}
                    />
                </div>
            }

            {/* <div className="card">
                <div className="mb-30 fz16 text-third">
                    {t('quick_actions')}
                </div>

                <div className="d-flex gap-16 w-100">
                    <button className="dark second" onClick={() => setAcceptanceProductionModal(true)}>
                        <FgWarehouseIcon />
                        {t('receipt_from_production')}
                    </button>

                    <button className="dark second" onClick={() => setShipmentDistributorModal(true)}>
                        <TruckIcon />
                        {t('distributor_shipment')}
                    </button>

                    <Link to="/warehouse-finished/orders" className="w-100">
                        <button className="white second">
                            <WarningIcon />
                            {t('process_orders')}
                        </button>
                    </Link>
                </div>
            </div> */}

            {/* <AcceptanceProduction modal={acceptanceProductionModal} setModal={setAcceptanceProductionModal} /> */}
            <ShipmentDistributor modal={shipmentDistributorModal} setModal={setShipmentDistributorModal} />
        </>
    )
}

export default Dashboard